import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Plus, X, Loader2, Pencil, Clock, User as UserIcon } from "lucide-react";

const inputCls = "w-full rounded-lg border border-[var(--njas-border)] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-njas-sage focus:border-njas-sage";
const API_ROOT = process.env.REACT_APP_BACKEND_URL + "/api";

export default function PeerWorkers() {
  const [rows, setRows] = useState([]);
  const [shifts, setShifts] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [u, s] = await Promise.all([
      api.get("/admin/users").catch(() => ({ data: [] })),
      api.get("/shifts").catch(() => ({ data: [] })),
    ]);
    const peers = (u.data || []).filter((x) => x.role === "peer_worker");
    setRows(peers);
    const byUser = {};
    (s.data || []).forEach((sh) => {
      const arr = byUser[sh.user_id] = byUser[sh.user_id] || { total_mins: 0, count: 0, last: null, active: null };
      arr.count += 1;
      arr.total_mins += sh.duration_minutes || 0;
      if (!arr.last || new Date(sh.clock_in) > new Date(arr.last.clock_in)) arr.last = sh;
      if (sh.status === "active") arr.active = sh;
    });
    setShifts(byUser);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (u) => {
    if (!window.confirm(`Delete peer worker ${u.name}? Their shift history will be preserved.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleActive = async (u) => {
    try { await api.patch(`/admin/users/${u.id}`, { is_active: !u.is_active }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="fade-in" data-testid="peer-workers-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="eyebrow text-njas-sage-hover text-[10px]">Team</div>
          <h2 className="font-serif text-3xl text-njas-navy">Peer Workers</h2>
          <p className="text-sm text-njas-muted mt-1">Paid mentors delivering direct client support. They clock on/off, submit case notes, incident reports and access assigned clients only.</p>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="new-peer-worker-btn" className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-5 py-2.5 text-sm hover:bg-njas-navy-hover">
          <Plus className="w-4 h-4" /> Add peer worker
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {loading && <div className="text-sm text-njas-muted">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="col-span-2 rounded-xl bg-white border border-[var(--njas-border)] p-8 text-center">
            <UserIcon className="w-10 h-10 text-njas-muted mx-auto mb-2" />
            <div className="font-serif text-lg text-njas-navy mb-1">No peer workers yet</div>
            <div className="text-sm text-njas-muted">Add your first peer worker to give them a login. They'll be able to clock on/off, view assigned clients, submit case notes, incidents and forms.</div>
          </div>
        )}
        {rows.map((u) => {
          const s = shifts[u.id] || { total_mins: 0, count: 0, active: null };
          const photoSrc = u.photo_path ? `${API_ROOT}/users/${u.id}/photo?v=${u.updated_at || ""}` : null;
          return (
            <div key={u.id} className="rounded-xl bg-white border border-[var(--njas-border)] p-5" data-testid={`peer-${u.id}`}>
              <div className="flex items-start gap-4 mb-3">
                <div className="w-14 h-14 rounded-full bg-njas-sage-tint text-njas-sage-hover overflow-hidden flex items-center justify-center">
                  {photoSrc ? <img src={photoSrc} alt={u.name} className="w-full h-full object-cover" /> : <UserIcon className="w-6 h-6" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-xl text-njas-navy">{u.name}</div>
                  <div className="text-xs text-njas-muted">{u.email}</div>
                  {u.position && <div className="text-xs text-njas-text mt-1">{u.position}</div>}
                  {s.active && (
                    <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-medium">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      On shift since {new Date(s.active.clock_in).toLocaleTimeString("en-AU")}
                      {s.active.client_name && ` · ${s.active.client_name}`}
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1">
                  <button onClick={() => setEditing(u)} className="p-1.5 text-njas-muted hover:text-njas-navy"><Pencil className="w-4 h-4" /></button>
                  {!u.is_active && <span className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-500 rounded uppercase tracking-wider">Inactive</span>}
                </div>
              </div>
              {u.bio && <p className="text-xs text-njas-text mb-3 whitespace-pre-wrap">{u.bio}</p>}
              {u.qualifications && <div className="text-[10px] text-njas-muted mb-3 uppercase tracking-wider">{u.qualifications}</div>}
              <div className="grid grid-cols-2 gap-3 text-sm border-t border-[var(--njas-border)] pt-3">
                <div>
                  <div className="text-[10px] text-njas-muted uppercase tracking-wider">Hours logged</div>
                  <div className="font-serif text-lg text-njas-navy"><Clock className="inline w-3.5 h-3.5 mr-1" />{(s.total_mins / 60).toFixed(1)}h</div>
                </div>
                <div>
                  <div className="text-[10px] text-njas-muted uppercase tracking-wider">Shifts</div>
                  <div className="font-serif text-lg text-njas-navy">{s.count}</div>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <button onClick={() => toggleActive(u)} className="text-xs text-njas-navy hover:underline">{u.is_active ? "Deactivate" : "Activate"}</button>
                <Link to="/dashboard/shifts" className="text-xs text-njas-navy hover:underline">View shifts →</Link>
                <button onClick={() => del(u)} className="text-xs text-red-600 hover:underline">Delete</button>
              </div>
            </div>
          );
        })}
      </div>

      {showAdd && <PeerWorkerModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {editing && <PeerWorkerModal initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function PeerWorkerModal({ initial, onClose, onSaved }) {
  const [f, set] = useState({
    name: initial?.name || "", email: initial?.email || "", password: "",
    phone: initial?.phone || "", position: initial?.position || "Peer Worker",
    bio: initial?.bio || "", qualifications: initial?.qualifications || "",
  });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => set({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (initial?.id) {
        const body = { name: f.name, email: f.email, phone: f.phone, position: f.position, bio: f.bio, qualifications: f.qualifications };
        if (f.password) body.password = f.password;
        await api.patch(`/admin/users/${initial.id}`, body);
      } else {
        if (!f.password || f.password.length < 4) { toast.error("Password required (min 4 chars)"); setBusy(false); return; }
        await api.post("/admin/users", { name: f.name, email: f.email, password: f.password, role: "peer_worker" });
        // now patch profile fields
        // fetch the newly created user by email so we can PATCH their profile
        const list = await api.get("/admin/users");
        const created = list.data.find((u) => u.email === f.email.toLowerCase().trim());
        if (created) {
          await api.patch(`/admin/users/${created.id}`, {
            phone: f.phone, position: f.position, bio: f.bio, qualifications: f.qualifications,
          });
        }
      }
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 py-10 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="eyebrow text-njas-sage-hover text-[10px]">Team</div>
            <h3 className="font-serif text-2xl text-njas-navy">{initial ? "Edit peer worker" : "Add peer worker"}</h3>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-njas-muted" /></button>
        </div>
        <form onSubmit={save} className="space-y-3" data-testid="peer-worker-form">
          <div className="grid grid-cols-2 gap-3">
            <label className="block col-span-2"><span className="text-xs uppercase tracking-wider text-njas-navy">Full name</span><input required value={f.name} onChange={upd("name")} className={inputCls} /></label>
            <label className="block col-span-2"><span className="text-xs uppercase tracking-wider text-njas-navy">Email (login)</span><input required type="email" value={f.email} onChange={upd("email")} className={inputCls} /></label>
            <label className="block col-span-2">
              <span className="text-xs uppercase tracking-wider text-njas-navy">{initial ? "Reset password (optional)" : "Password"}</span>
              <input type="text" required={!initial} value={f.password} onChange={upd("password")} placeholder={initial ? "Leave blank to keep current" : ""} className={inputCls} />
            </label>
            <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">Phone</span><input value={f.phone} onChange={upd("phone")} className={inputCls} /></label>
            <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">Position / title</span><input value={f.position} onChange={upd("position")} className={inputCls} /></label>
            <label className="block col-span-2"><span className="text-xs uppercase tracking-wider text-njas-navy">Qualifications</span><input value={f.qualifications} onChange={upd("qualifications")} placeholder="e.g. Cert IV in Community Services, lived experience" className={inputCls} /></label>
            <label className="block col-span-2"><span className="text-xs uppercase tracking-wider text-njas-navy">Bio</span><textarea rows={3} value={f.bio} onChange={upd("bio")} className={inputCls} /></label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-full px-5 py-2 text-sm text-njas-muted hover:text-njas-navy">Cancel</button>
            <button type="submit" disabled={busy} data-testid="peer-worker-submit" className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-6 py-2 text-sm hover:bg-njas-navy-hover disabled:opacity-60">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} {initial ? "Save changes" : "Create peer worker"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
